import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Divider,
  Flex,
  FormControl,
  FormLabel,
  Icon,
  Input,
  InputGroup,
  InputLeftElement,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Spinner,
  Switch,
  Text,
  useDisclosure,
  useToast,
  VStack,
} from '@chakra-ui/react';
import { MdSearch } from 'react-icons/md';
import { useTranslation } from 'i18n/TranslationProvider';
import { PageShell, PageHeader } from 'components/layout/PageShell';
import { UnifiedLoadingState } from 'components/layout/UnifiedPrimitives';
import {
  getPublicSettings,
  updateSettings,
  getAiUsers,
  getPaymentDelayedUsers,
  searchUsers,
  bulkSetAiStatus,
} from 'services/settingsService';
import useChangeUserAiStatus from 'domains/admin/hooks/useChangeUserAiStatus';
import useChangeUserPaymentDelayed from 'domains/admin/hooks/useChangeUserPaymentDelayed';

export default function AdminSettings() {
  const { t } = useTranslation();
  const toast = useToast();
  const { isOpen, onOpen, onClose } = useDisclosure();
  const {
    isOpen: isPaymentDelayedOpen,
    onOpen: onPaymentDelayedOpen,
    onClose: onPaymentDelayedClose,
  } = useDisclosure();

  // Page state
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Translation feature flag
  const [translationEnabled, setTranslationEnabled] = useState(false);
  const [translationSaving, setTranslationSaving] = useState(false);

  // Address autocomplete feature flag
  const [autocompleteEnabled, setAutocompleteEnabled] = useState(true);
  const [autocompleteSaving, setAutocompleteSaving] = useState(false);

  // User management state
  const [aiUsers, setAiUsers] = useState([]);
  const [totalUserCount, setTotalUserCount] = useState(0);
  const [paymentDelayedUsers, setPaymentDelayedUsers] = useState([]);

  // Search state (shared by both modals — only one modal open at a time)
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const debounceRef = useRef(null);

  const { changeUserAiStatus, loading: assignLoading } = useChangeUserAiStatus();
  const {
    changeUserPaymentDelayed,
    loading: paymentDelayedAssignLoading,
  } = useChangeUserPaymentDelayed();
  const allEnabled = totalUserCount > 0 && aiUsers.length === totalUserCount;

  const fetchAiUsers = useCallback(async () => {
    try {
      const data = await getAiUsers();
      setAiUsers(data.users);
      setTotalUserCount(data.totalUserCount);
    } catch {
      // silent — toast on explicit actions only
    }
  }, []);

  const fetchPaymentDelayedUsers = useCallback(async () => {
    try {
      const data = await getPaymentDelayedUsers();
      setPaymentDelayedUsers(data.users);
      setTotalUserCount(data.totalUserCount);
    } catch {
      // silent — toast on explicit actions only
    }
  }, []);

  useEffect(() => {
    let cancelled = false;

    Promise.all([getAiUsers(), getPaymentDelayedUsers(), getPublicSettings()])
      .then(([aiData, paymentDelayedData, publicData]) => {
        if (cancelled) return;
        setAiUsers(aiData.users);
        setTotalUserCount(aiData.totalUserCount);
        setPaymentDelayedUsers(paymentDelayedData.users);
        setTranslationEnabled(publicData.liveTranslationEnabled === true);
        setAutocompleteEnabled(publicData.addressAutocompleteEnabled !== false);
      })
      .catch(() => {
        toast({ title: t('Failed to load settings.'), status: 'error', duration: 3000 });
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search
  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (searchQuery.length < 2) {
      setSearchResults([]);
      return;
    }

    debounceRef.current = setTimeout(async () => {
      setSearching(true);
      try {
        const data = await searchUsers(searchQuery);
        setSearchResults(data.users);
      } catch {
        setSearchResults([]);
      } finally {
        setSearching(false);
      }
    }, 300);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [searchQuery]);

  const handleToggleAll = async () => {
    const newValue = !allEnabled;
    setSaving(true);
    try {
      await Promise.all([
        updateSettings({ aiEnabled: newValue }),
        bulkSetAiStatus(newValue),
      ]);
      await fetchAiUsers();
      toast({
        title: newValue
          ? t('AI assistant enabled for all users.')
          : t('AI assistant disabled for all users.'),
        status: 'success',
        duration: 3000,
      });
    } catch {
      toast({ title: t('Failed to update setting.'), status: 'error', duration: 3000 });
    } finally {
      setSaving(false);
    }
  };

  const handleAssignUser = async (firebaseUid) => {
    const success = await changeUserAiStatus(firebaseUid, true);
    if (success) {
      toast({ title: t('AI enabled for user.'), status: 'success', duration: 2000 });
      await fetchAiUsers();
      // Refresh search results to update the enable/disable state
      if (searchQuery.length >= 2) {
        try {
          const data = await searchUsers(searchQuery);
          setSearchResults(data.users);
        } catch { /* silent */ }
      }
    } else {
      toast({ title: t('Failed to enable AI for user.'), status: 'error', duration: 3000 });
    }
  };

  const handleRemoveUser = async (firebaseUid) => {
    const success = await changeUserAiStatus(firebaseUid, false);
    if (success) {
      toast({ title: t('AI disabled for user.'), status: 'success', duration: 2000 });
      await fetchAiUsers();
      if (searchQuery.length >= 2) {
        try {
          const data = await searchUsers(searchQuery);
          setSearchResults(data.users);
        } catch { /* silent */ }
      }
    } else {
      toast({ title: t('Failed to disable AI for user.'), status: 'error', duration: 3000 });
    }
  };

  const handleAssignPaymentDelayedUser = async (firebaseUid) => {
    const success = await changeUserPaymentDelayed(firebaseUid, true);
    if (success) {
      toast({ title: t('Delayed payments enabled for user.'), status: 'success', duration: 2000 });
      await fetchPaymentDelayedUsers();
      if (searchQuery.length >= 2) {
        try {
          const data = await searchUsers(searchQuery);
          setSearchResults(data.users);
        } catch { /* silent */ }
      }
    } else {
      toast({ title: t('Failed to enable delayed payments for user.'), status: 'error', duration: 3000 });
    }
  };

  const handleRemovePaymentDelayedUser = async (firebaseUid) => {
    const success = await changeUserPaymentDelayed(firebaseUid, false);
    if (success) {
      toast({ title: t('Delayed payments disabled for user.'), status: 'success', duration: 2000 });
      await fetchPaymentDelayedUsers();
      if (searchQuery.length >= 2) {
        try {
          const data = await searchUsers(searchQuery);
          setSearchResults(data.users);
        } catch { /* silent */ }
      }
    } else {
      toast({ title: t('Failed to disable delayed payments for user.'), status: 'error', duration: 3000 });
    }
  };

  const handleToggleTranslation = async () => {
    const newValue = !translationEnabled;
    setTranslationSaving(true);
    try {
      await updateSettings({ liveTranslationEnabled: newValue });
      setTranslationEnabled(newValue);
      toast({
        title: newValue
          ? t('Live translation enabled.')
          : t('Live translation disabled.'),
        status: 'success',
        duration: 3000,
      });
    } catch {
      toast({ title: t('Failed to update setting.'), status: 'error', duration: 3000 });
    } finally {
      setTranslationSaving(false);
    }
  };

  const handleToggleAutocomplete = async () => {
    const newValue = !autocompleteEnabled;
    setAutocompleteSaving(true);
    try {
      await updateSettings({ addressAutocompleteEnabled: newValue });
      setAutocompleteEnabled(newValue);
      toast({
        title: newValue
          ? t('Address autocomplete enabled.')
          : t('Address autocomplete disabled.'),
        status: 'success',
        duration: 3000,
      });
    } catch {
      toast({ title: t('Failed to update setting.'), status: 'error', duration: 3000 });
    } finally {
      setAutocompleteSaving(false);
    }
  };

  const aiUserIds = new Set(aiUsers.map((u) => u.firebaseUid));
  const paymentDelayedUserIds = new Set(paymentDelayedUsers.map((u) => u.firebaseUid));

  if (loading) {
    return (
      <PageShell>
        <UnifiedLoadingState label={t('Loading settings...')} minH="200px" />
      </PageShell>
    );
  }

  return (
    <PageShell>
      <VStack align="stretch" spacing={6}>
        <PageHeader
          title={t('Platform Settings')}
          description={t('Manage platform-wide feature flags and configuration.')}
        />

        <Box borderWidth="1px" borderRadius="2xl" p={6} bg="white" borderColor="gray.200">
          <Text fontSize="lg" fontWeight="700" mb={4} color="navy.700">
            {t('Feature Flags')}
          </Text>

          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box flex="1">
              <FormLabel htmlFor="ai-enabled" mb={0} fontWeight="600">
                {t('AI Assistant')}
              </FormLabel>
              <Text fontSize="sm" color="gray.500">
                {aiUsers.length} / {totalUserCount} {t('users enabled')}
              </Text>
            </Box>
            <Flex align="center" gap={3}>
              <Button size="sm" variant="outline" colorScheme="blue" onClick={onOpen}>
                {t('Manage Users')}
              </Button>
              <Switch
                id="ai-enabled"
                colorScheme="blue"
                size="lg"
                isChecked={allEnabled}
                isDisabled={saving}
                onChange={handleToggleAll}
              />
            </Flex>
          </FormControl>

          <Divider my={4} />

          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box flex="1">
              <FormLabel mb={0} fontWeight="600">
                {t('Delayed Payments')}
              </FormLabel>
              <Text fontSize="sm" color="gray.500">
                {t(
                  'Monthly-contract clients who skip per-listing payment. {count} {label} enabled.',
                ).replace('{count}', paymentDelayedUsers.length).replace(
                  '{label}',
                  paymentDelayedUsers.length === 1 ? t('user') : t('users'),
                )}
              </Text>
            </Box>
            <Flex align="center" gap={3}>
              <Button
                size="sm"
                variant="outline"
                colorScheme="purple"
                onClick={() => {
                  setSearchQuery('');
                  setSearchResults([]);
                  onPaymentDelayedOpen();
                }}
              >
                {t('Manage Users')}
              </Button>
            </Flex>
          </FormControl>

          <Divider my={4} />

          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box flex="1">
              <FormLabel htmlFor="translation-enabled" mb={0} fontWeight="600">
                {t('Live Translation')}
              </FormLabel>
              <Text fontSize="sm" color="gray.500">
                {t('AI-powered message translation between languages')}
              </Text>
            </Box>
            <Switch
              id="translation-enabled"
              colorScheme="blue"
              size="lg"
              isChecked={translationEnabled}
              isDisabled={translationSaving}
              onChange={handleToggleTranslation}
            />
          </FormControl>

          <Divider my={4} />

          <FormControl display="flex" alignItems="center" justifyContent="space-between">
            <Box flex="1">
              <FormLabel htmlFor="autocomplete-enabled" mb={0} fontWeight="600">
                {t('Address Autocomplete')}
              </FormLabel>
              <Text fontSize="sm" color="gray.500">
                {t(
                  'Google Places suggestions when creating listings. Turn off to stop billable Maps API calls platform-wide.',
                )}
              </Text>
            </Box>
            <Switch
              id="autocomplete-enabled"
              colorScheme="blue"
              size="lg"
              isChecked={autocompleteEnabled}
              isDisabled={autocompleteSaving}
              onChange={handleToggleAutocomplete}
            />
          </FormControl>
        </Box>
      </VStack>

      {/* Manage Users Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="lg" scrollBehavior="inside">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Manage AI Assistant Users')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Search */}
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder={t('Search users by email...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            {/* Search results */}
            {searching && (
              <Flex justify="center" py={3}>
                <Spinner size="sm" />
              </Flex>
            )}
            {!searching && searchResults.length > 0 && (
              <Box mb={4}>
                <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">
                  {t('Search Results')}
                </Text>
                <VStack align="stretch" spacing={2}>
                  {searchResults.map((user) => {
                    const isEnabled = aiUserIds.has(user.firebaseUid);
                    return (
                      <Flex
                        key={user.firebaseUid}
                        align="center"
                        justify="space-between"
                        p={2}
                        borderRadius="md"
                        bg="gray.50"
                      >
                        <Box>
                          <Text fontSize="sm" fontWeight="500">{user.email}</Text>
                          <Flex gap={2} align="center">
                            <Text fontSize="xs" color="gray.500">{user.displayName || '-'}</Text>
                            <Badge fontSize="xs">{user.role}</Badge>
                          </Flex>
                        </Box>
                        <Button
                          size="xs"
                          colorScheme={isEnabled ? 'red' : 'blue'}
                          variant={isEnabled ? 'outline' : 'solid'}
                          isLoading={assignLoading}
                          onClick={() => isEnabled ? handleRemoveUser(user.firebaseUid) : handleAssignUser(user.firebaseUid)}
                        >
                          {isEnabled ? t('Remove') : t('Enable')}
                        </Button>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            )}
            {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
              <Text fontSize="sm" color="gray.500" mb={4}>
                {t('No users found.')}
              </Text>
            )}

            <Divider mb={4} />

            {/* Enabled Users List */}
            <Flex align="center" gap={2} mb={3}>
              <Text fontSize="sm" fontWeight="600" color="gray.600">
                {t('Enabled Users')}
              </Text>
              <Badge colorScheme="blue">{aiUsers.length}</Badge>
            </Flex>

            {aiUsers.length === 0 ? (
              <Text fontSize="sm" color="gray.400">
                {t('No users have AI enabled.')}
              </Text>
            ) : (
              <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
                {aiUsers.map((user) => (
                  <Flex
                    key={user.firebaseUid}
                    align="center"
                    justify="space-between"
                    p={2}
                    borderRadius="md"
                    bg="gray.50"
                  >
                    <Box>
                      <Text fontSize="sm" fontWeight="500">{user.email}</Text>
                      <Flex gap={2} align="center">
                        <Text fontSize="xs" color="gray.500">{user.displayName || '-'}</Text>
                        <Badge fontSize="xs">{user.role}</Badge>
                      </Flex>
                    </Box>
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="outline"
                      isLoading={assignLoading}
                      onClick={() => handleRemoveUser(user.firebaseUid)}
                    >
                      {t('Remove')}
                    </Button>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onClose}>{t('Close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Manage Delayed Payment Users Modal */}
      <Modal
        isOpen={isPaymentDelayedOpen}
        onClose={onPaymentDelayedClose}
        size="lg"
        scrollBehavior="inside"
      >
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{t('Manage Delayed Payment Users')}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            {/* Search */}
            <InputGroup mb={4}>
              <InputLeftElement pointerEvents="none">
                <Icon as={MdSearch} color="gray.400" />
              </InputLeftElement>
              <Input
                placeholder={t('Search users by email...')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
              />
            </InputGroup>

            {/* Search results */}
            {searching && (
              <Flex justify="center" py={3}>
                <Spinner size="sm" />
              </Flex>
            )}
            {!searching && searchResults.length > 0 && (
              <Box mb={4}>
                <Text fontSize="sm" fontWeight="600" mb={2} color="gray.600">
                  {t('Search Results')}
                </Text>
                <VStack align="stretch" spacing={2}>
                  {searchResults.map((user) => {
                    const isEnabled = paymentDelayedUserIds.has(user.firebaseUid);
                    return (
                      <Flex
                        key={user.firebaseUid}
                        align="center"
                        justify="space-between"
                        p={2}
                        borderRadius="md"
                        bg="gray.50"
                      >
                        <Box>
                          <Text fontSize="sm" fontWeight="500">{user.email}</Text>
                          <Flex gap={2} align="center">
                            <Text fontSize="xs" color="gray.500">{user.displayName || '-'}</Text>
                            <Badge fontSize="xs">{user.role}</Badge>
                          </Flex>
                        </Box>
                        <Button
                          size="xs"
                          colorScheme={isEnabled ? 'red' : 'purple'}
                          variant={isEnabled ? 'outline' : 'solid'}
                          isLoading={paymentDelayedAssignLoading}
                          onClick={() =>
                            isEnabled
                              ? handleRemovePaymentDelayedUser(user.firebaseUid)
                              : handleAssignPaymentDelayedUser(user.firebaseUid)
                          }
                        >
                          {isEnabled ? t('Remove') : t('Enable')}
                        </Button>
                      </Flex>
                    );
                  })}
                </VStack>
              </Box>
            )}
            {!searching && searchQuery.length >= 2 && searchResults.length === 0 && (
              <Text fontSize="sm" color="gray.500" mb={4}>
                {t('No users found.')}
              </Text>
            )}

            <Divider mb={4} />

            {/* Enabled Users List */}
            <Flex align="center" gap={2} mb={3}>
              <Text fontSize="sm" fontWeight="600" color="gray.600">
                {t('Enabled Users')}
              </Text>
              <Badge colorScheme="purple">{paymentDelayedUsers.length}</Badge>
            </Flex>

            {paymentDelayedUsers.length === 0 ? (
              <Text fontSize="sm" color="gray.400">
                {t('No users have delayed payments enabled.')}
              </Text>
            ) : (
              <VStack align="stretch" spacing={2} maxH="300px" overflowY="auto">
                {paymentDelayedUsers.map((user) => (
                  <Flex
                    key={user.firebaseUid}
                    align="center"
                    justify="space-between"
                    p={2}
                    borderRadius="md"
                    bg="gray.50"
                  >
                    <Box>
                      <Text fontSize="sm" fontWeight="500">{user.email}</Text>
                      <Flex gap={2} align="center">
                        <Text fontSize="xs" color="gray.500">{user.displayName || '-'}</Text>
                        <Badge fontSize="xs">{user.role}</Badge>
                      </Flex>
                    </Box>
                    <Button
                      size="xs"
                      colorScheme="red"
                      variant="outline"
                      isLoading={paymentDelayedAssignLoading}
                      onClick={() => handleRemovePaymentDelayedUser(user.firebaseUid)}
                    >
                      {t('Remove')}
                    </Button>
                  </Flex>
                ))}
              </VStack>
            )}
          </ModalBody>
          <ModalFooter>
            <Button onClick={onPaymentDelayedClose}>{t('Close')}</Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </PageShell>
  );
}
